import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Copy, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read-only staff details for the Payroll department (also usable by admin):
// full name, email, DOB, address, NI number + proof docs, bank details.
export const StaffPayrollInfo = ({ employeeId }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/employees/${employeeId}/payroll-info`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load staff details'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId]);

  const viewProof = async (doc) => {
    try {
      const r = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Failed to open document'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground italic">Loading…</p>;
  if (!info) return <p className="text-sm text-muted-foreground italic">No data available.</p>;

  const statusBadge = (s) => ({
    approved: 'bg-green-500/15 text-green-500 border-green-500/40',
    pending: 'bg-amber-500/15 text-amber-500 border-amber-500/40',
    rejected: 'bg-red-500/15 text-red-500 border-red-500/40',
  }[s] || 'bg-muted text-muted-foreground border-border');

  return (
    <div className="space-y-2.5" data-testid="staff-payroll-info">
      <InfoRow label="Full Name" value={info.full_name} testId="spi-name" />
      <InfoRow label="Employee Code" value={info.employee_code} testId="spi-code" mono />
      <InfoRow label="Status" value={info.status} testId="spi-status" />
      <InfoRow label="Email" value={info.email} testId="spi-email" mono />
      <InfoRow label="Phone" value={info.phone} testId="spi-phone" mono />
      <InfoRow label="Date of Birth" value={info.date_of_birth} testId="spi-dob" mono />
      <InfoRow label="Address" value={info.address} testId="spi-address" />
      <InfoRow label="NI Number" value={info.ni_number} testId="spi-ni" mono highlight />
      <div className="border-t border-border/60 pt-2.5 space-y-2.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Bank Details</p>
        <InfoRow label="Account Holder" value={info.bank.account_holder} testId="spi-bank-holder" />
        <InfoRow label="Sort Code" value={info.bank.sort_code} testId="spi-bank-sort" mono />
        <InfoRow label="Account Number" value={info.bank.account_number} testId="spi-bank-account" mono />
      </div>
      <div className="border-t border-border/60 pt-2.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Proof of National Insurance Number</p>
        {info.ni_proofs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="spi-proof-empty">No proof uploaded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {info.ni_proofs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-2 bg-muted/30 border border-border/60 rounded-sm px-2 py-1.5" data-testid={`spi-proof-${doc.id}`}>
                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <span className="text-xs text-foreground truncate flex-1">{doc.file_name}</span>
                <Badge className={`text-[9px] uppercase tracking-wider border ${statusBadge(doc.status)}`}>
                  {String(doc.status || '').replace(/_/g, ' ')}
                </Badge>
                <Button
                  variant="outline" size="sm" onClick={() => viewProof(doc)}
                  className="h-6 px-2 text-[10px] border-border rounded-sm"
                  data-testid={`spi-proof-view-${doc.id}`}
                >
                  View
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground italic pt-1">
        Read-only — entered by the staff member in My Profile. Use the copy buttons; nothing can be edited here.
      </p>
    </div>
  );
};

function InfoRow({ label, value, testId, mono, highlight }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-28 flex-shrink-0">{label}</span>
      <span className={`text-sm truncate flex-1 ${mono ? 'font-mono' : ''} ${highlight ? 'text-primary font-semibold' : 'text-foreground'}`} data-testid={testId}>
        {value || '— not provided —'}
      </span>
      {value && (
        <button type="button" onClick={copy} className="text-muted-foreground hover:text-primary flex-shrink-0" data-testid={`${testId}-copy`}>
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

export default StaffPayrollInfo;
